import { StepBase } from './StepBase'
import { PageStepType } from './PageTypes';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { TaskDataRecord, TaskManager } from '../Tasks/TaskManager';
import { DataRecord } from '../utils/DataRecord';
import { DataTools } from '../utils/DataTools';
import { DataQuery } from '../utils/DataQuery';

export class StepTask extends StepBase
{

	/** Expire the task */
	async processExpireTaskID(logic: LogicEngine): Promise<boolean>
	{
		if (!this.raw.text_en)
			return false;

		let id = logic.processTextReplacement(this.option_1);
		let reason = logic.processTextReplacement(this.raw.text_en);

		const tm = new TaskManager();
		let task = await tm.getTaskDataById(id);
		if (task.record.data.task_status === 'P')
		{
			console.log("StepTask processExpireTaskID ExpireByID: ", id, " Reason:", reason);
			await task.setStatus("E", reason);
		} else
		{
			if (LogicEngine.debugOutput)
				console.log("StepTask processExpireTaskID Not Pending:", task.record.data.task_status, " ExpireByID: ", id, " Reason:", reason);
		}

		return true;
	}


	/** Preload all tasks for a given participant id specified in option_1 */
	async processCacheLoad(logic: LogicEngine): Promise<boolean>
	{
		let tm = new TaskManager();
		await tm.loadAllTaskByParticipant(logic.processTextReplacement(this.option_1));
		return true;
	}

	/**
	 * Set the date and time for the task to expire as well as the date/time for the task to be visible.
	 * start time is option_1
	 * end time is option_2
	 * Both times should be a date value in participant local time.
	 * @param logic
	 */
	async processSetExpire(logic: LogicEngine): Promise<boolean>
	{
		let start_time = DataTools.getNumber(logic.processTextReplacement(this.option_1));
		let end_time = DataTools.getNumber(logic.processTextReplacement(this.option_2));
		let id = logic.processTextReplacement(this.text);

		console.log("StepTask processSetExpire StepTask Set Expire ", start_time, " to ", end_time);

		const tm = new TaskManager();
		let task = await tm.getTaskDataById(id);
		if (!task || !task.record || !task.record.data || !task.record.data.id)
		{
			const searchTask = await tm.loadByTitle(logic.process("participant_uuid"), id);
			if (searchTask && searchTask.data && searchTask.data.id)
			{
				task = searchTask.data;
			}
		}

		// Don't worry about anything if the values are already set
		if (task.record.data.timestamp_start == start_time &&
			task.record.data.timestamp_end == end_time)
			return true;

		task.record.data.timestamp_start = start_time;
		task.record.data.timestamp_end = end_time;
		await task.record.saveIfNeeded();

		return true;
	}

	/** Expire the task */
	async processCancelTask(logic: LogicEngine): Promise<boolean>
	{
		if (!this.raw.text_en)
			return false;

		let id = logic.processTextReplacement(this.option_1);
		let reason = logic.processTextReplacement(this.raw.text_en);

		console.log("StepTask processCancelTask Canceled: ", id, " Reason:", reason);
		const tm = new TaskManager();
		let task = await tm.getTaskDataById(id);
		await task.setStatus("X", reason);
		return true;
	}

	/* Given a task, import the values into the logic engine */
	importTask(logic: LogicEngine, task: DataRecord<TaskDataRecord> | undefined)
	{
		let taskValues: any = {
			"last_task_id": "",
			"created_dt": "",
			"created_by": "",
			"task_status": "",
			"is_enabled": false,
			"is_blocking": false,
			"title_en": "",
			"subtitle_en": "",
			"task_icon": "",
			"task_route": "",
			"task_name": "",
			"lock_status": "",
			"lock_by": "",
			"lock_date": ""
		}

		if (task === undefined || task === null)
		{
			logic.import(taskValues);
			return;
		}

		// task was found. Import the values
		for (let key of Object.keys(taskValues))
		{
			taskValues[key] = task.data[key];
		}

		//  import last task id value from data
		taskValues["last_task_id"] = task.data.id;
		logic.import(taskValues);
	}

	/** Check to see if a task was imported because last_task_id exists.
	If so, import the task data record associated with it.
	*/
	async processLoadDataRecord(logic: LogicEngine): Promise<boolean>
	{
		let task_id = DataTools.internalValidateString(logic.process("last_task_id"));
		if (task_id === "")
			task_id = DataTools.internalValidateString(logic.process("task_id"));

		if (task_id === "")
		{
			console.error("Error: processLoadDataRecord missing last_task_id and task_id");
			return false;
		}

		let table_name = DataTools.internalValidateString(logic.process("task_name"));
		if (table_name === "")
		{
			console.error("Error: processLoadDataRecord missing task_name to find table. ");
			return false;
		}

		let results = await DataQuery.instance().executeQuery(table_name, "task_id=" + task_id);
		if (!results || results.length == 0)
		{
			console.warn("Error: processLoadDataRecord no task data for table=", table_name, "task_id=", task_id);
			return false;
		}

		for (const result of results)
		{
			logic.import(result.data);
		}

		return true;
	}

	/** Load a task by title
	@example
	Task  Options=LoadByTitle Text=Task Title
	*/
	async processLoadTaskByTitle(logic: LogicEngine): Promise<boolean>
	{
		if (!this.raw.text_en)
			return false;

		const tm = new TaskManager();

		// Get the UUID from the "option_1" field
		let uuid = logic.processTextReplacement(this.option_1);
		if (!uuid) uuid = logic.process("participant_uuid");

		// Get the task name from the text_en field
		let taskName = logic.processTextReplacement(this.text);

		// try to find the task with task manager
		let task = await tm.loadByTitle(uuid, taskName);
		if (task === undefined)
		{
			console.warn("Unable to find task:", taskName, "for", uuid);
			this.importTask(logic, undefined);
			return false;
		}

		this.importTask(logic, task);
		return true;
	}

	/** Update the task status and create an audit record */
	async processUpdateStatus(logic: LogicEngine): Promise<boolean>
	{
		let newStatus = DataTools.internalValidateString(this.text);

		let task_id = DataTools.internalValidateString(logic.process("last_task_id"));
		if (task_id === "")
			task_id = DataTools.internalValidateString(logic.process("task_id"));

		if (task_id === "")
		{
			console.error("Error: processUpdateStatus missing last_task_id and task_id");
			return false;
		}

		const reason = DataTools.internalValidateString(this.option_1);

		const tm = new TaskManager();
		let task = await tm.getTaskDataById(task_id);
		await task.setStatus(newStatus, reason);
		this.importTask(logic, task.record);

		return true;
	}

	async processStep(logic: LogicEngine): Promise<boolean>
	{
		if (this.options.checkOption("Create"))
		{
			/* Create a new task based on the configuration.  The text column
			   lists the name of the task from the tasks table that needs to be created. */

			if (!this.raw.option_1)
			{
				console.log("StepTask processSet missing option_1, should be the participant id");
				return false;
			}

			const participantId = logic.processTextReplacement(this.raw.option_1);
			const taskName = this.text;

			/* Use the task manager to create the task and update the task count */
			const tm = new TaskManager();
			const task = await tm.getTask(taskName);
			await task.createTaskForParticipant(participantId, logic, this.raw.option_2);

		} else if (this.options.checkOption("Cancel"))
		{
			// Cancel the task
			return await this.processCancelTask(logic);
		} else if (this.options.checkOption("CacheLoad"))
		{
			// Cancel the task
			return await this.processCacheLoad(logic);
		} else if (this.options.checkOption("Expire"))
		{

			//  Task ID is specified in the text_en column
			return await this.processExpireTaskID(logic);

		} else if (this.options.checkOption("SetExpire"))
		{
			return await this.processSetExpire(logic);
		} else if (this.options.checkOption("LoadByTitle"))
		{
			/** Load a task by title where the title of the task is specified in task_en */
			return await this.processLoadTaskByTitle(logic);

		} else if (this.options.checkOption("LoadByTitleWithData"))
		{
			/** Load a task by title where the title of the task is specified in task_en */
			await this.processLoadTaskByTitle(logic);
			return await this.processLoadDataRecord(logic);

		} else if (this.options.checkOption("LoadData"))
		{
			return await this.processLoadDataRecord(logic);

		} else if (this.options.checkOption("UpdateStatus"))
		{
			return await this.processUpdateStatus(logic);

		} else
		{
			console.log("StepTask processStep, unknown action type for task:", this.raw);
		}

		return true;
	}

}

